import { useCallback, useMemo, useSyncExternalStore } from 'react';
import type { JupyterProviderSelection } from '@sharpe/shared/server';

const STORAGE_KEY = 'sharpe-preferred-provider-selection';
const SELECTION_CHANGED_EVENT = 'sharpe-preferred-provider-selection-changed';

function getSnapshot(): string | null {
  return localStorage.getItem(STORAGE_KEY);
}

function getServerSnapshot(): string | null {
  return null;
}

function subscribe(callback: () => void): () => void {
  const handleChange = () => callback();
  window.addEventListener(SELECTION_CHANGED_EVENT, handleChange);
  window.addEventListener('storage', handleChange);
  return () => {
    window.removeEventListener(SELECTION_CHANGED_EVENT, handleChange);
    window.removeEventListener('storage', handleChange);
  };
}

function notifySelectionChanged(): void {
  window.dispatchEvent(new CustomEvent(SELECTION_CHANGED_EVENT));
}

function parseSelectionSnapshot(snapshot: string | null): JupyterProviderSelection {
  if (!snapshot) {
    return { type: 'default' };
  }

  try {
    const parsed = JSON.parse(snapshot) as JupyterProviderSelection;
    if (parsed.type === 'cloud') return parsed;
    if (parsed.type === 'default') return parsed;
    if (parsed.type === 'self-hosted' && parsed.providerId) return parsed;
  } catch {
    // Fall back to the legacy providerId-only value.
    return { type: 'self-hosted', providerId: snapshot };
  }

  return { type: 'default' };
}

function serializeSelection(selection: JupyterProviderSelection): string | null {
  if (selection.type === 'default') {
    return null;
  }

  return JSON.stringify(selection);
}

export function isSelfHostedSelection(
  selection: JupyterProviderSelection
): selection is Extract<JupyterProviderSelection, { type: 'self-hosted' }> {
  return selection.type === 'self-hosted';
}

/**
 * Per-user provider preference stored in localStorage.
 * An explicit selection overrides default provider resolution.
 */
export function usePreferredProvider() {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const preferredProviderSelection = useMemo(() => parseSelectionSnapshot(snapshot), [snapshot]);

  const setPreferredProviderSelection = useCallback((selection: JupyterProviderSelection) => {
    const serialized = serializeSelection(selection);

    if (serialized === null) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, serialized);
    }

    notifySelectionChanged();
  }, []);

  return { preferredProviderSelection, setPreferredProviderSelection };
}
